/**
 * Vendor Bundle Offer
 * Handles custom bundle offers where only the bundle item receives a discount
 */

class VendorBundleOffer {
  constructor(container) {
    this.container = container;
    this.mainProductId = container.dataset.mainProductId;
    this.bundleProductId = container.dataset.bundleProductId;

    // Elements
    this.bundleCheckbox = container.querySelector('[data-bundle-checkbox]');
    this.variantSelect = container.querySelector('[data-bundle-variant-select]');
    this.addButton = container.querySelector('[data-bundle-add-button]');
    this.totalElement = container.querySelector('[data-bundle-total]');
    this.discountedPriceElement = container.querySelector('[data-bundle-discounted-price]');
    this.originalPriceElement = container.querySelector('[data-bundle-original-price]');

    // State
    this.discountRate = parseFloat(this.discountedPriceElement?.dataset.discountRate || 20);
    this.mainPrice = parseInt(container.querySelector('[data-main-price]')?.dataset.mainPrice || 0);
    this.bundleIncluded = this.bundleCheckbox?.checked || false;

    this.init();
  }

  init() {
    // Event listeners
    if (this.bundleCheckbox) {
      this.bundleCheckbox.addEventListener('change', this.handleCheckboxChange.bind(this));
    }

    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', this.handleVariantChange.bind(this));
    }

    if (this.addButton) {
      this.addButton.addEventListener('click', this.handleAddToCart.bind(this));
    }

    // Listen for main product variant changes
    this.observeMainProductVariant();

    // Initial calculation
    this.updateTotal();
  }

  /**
   * Handle bundle checkbox toggle
   */
  handleCheckboxChange(event) {
    this.bundleIncluded = event.target.checked;
    this.updateTotal();
    this.updateButtonState();
  }

  /**
   * Handle variant selection change
   */
  handleVariantChange(event) {
    const selectedOption = event.target.selectedOptions[0];
    const newPrice = parseInt(selectedOption.dataset.price);

    // Update prices
    this.updateBundlePrice(newPrice);
    this.updateTotal();

    // Update button data
    if (this.addButton) {
      this.addButton.dataset.bundleVariantId = event.target.value;
    }
  }

  /**
   * Update bundle product price display
   */
  updateBundlePrice(originalPrice) {
    const discountAmount = Math.round(originalPrice * this.discountRate / 100);
    const discountedPrice = originalPrice - discountAmount;

    if (this.originalPriceElement) {
      this.originalPriceElement.textContent = this.formatMoney(originalPrice);
      this.originalPriceElement.dataset.bundleOriginalPrice = originalPrice;
    }

    if (this.discountedPriceElement) {
      this.discountedPriceElement.textContent = this.formatMoney(discountedPrice);
      this.discountedPriceElement.dataset.bundleDiscountedPrice = discountedPrice;
    }
  }

  /**
   * Update total price
   */
  updateTotal() {
    if (!this.totalElement) return;

    let total = this.mainPrice;

    if (this.bundleIncluded && this.discountedPriceElement) {
      const bundlePrice = parseInt(this.discountedPriceElement.dataset.bundleDiscountedPrice);
      total += bundlePrice;
    }

    this.totalElement.textContent = this.formatMoney(total);
  }

  /**
   * Update button state
   */
  updateButtonState() {
    if (!this.addButton) return;

    if (this.bundleIncluded) {
      this.addButton.querySelector('.vendor-bundle-offer__button-text').textContent = 'Add Bundle to Cart';
    } else {
      this.addButton.querySelector('.vendor-bundle-offer__button-text').textContent = 'Add Main Product Only';
    }
  }

  /**
   * Handle add to cart
   */
  async handleAddToCart(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const originalText = button.querySelector('.vendor-bundle-offer__button-text').textContent;

    // Disable button
    button.disabled = true;
    button.classList.add('loading');
    button.querySelector('.vendor-bundle-offer__button-text').textContent = 'Adding...';

    try {
      const items = [];

      // Add main product
      const mainVariantId = this.getMainProductVariantId();
      items.push({
        id: mainVariantId,
        quantity: 1,
        properties: {
          '_bundle_purchase': 'true',
          '_bundle_type': 'main_product'
        }
      });

      // Add bundle product if checked
      if (this.bundleIncluded) {
        const bundleVariantId = button.dataset.bundleVariantId;
        const originalPrice = parseInt(this.originalPriceElement.dataset.bundleOriginalPrice);
        const discountedPrice = parseInt(this.discountedPriceElement.dataset.bundleDiscountedPrice);
        const savings = originalPrice - discountedPrice;

        items.push({
          id: bundleVariantId,
          quantity: 1,
          properties: {
            '_bundle_purchase': 'true',
            '_bundle_type': 'discounted_addon',
            '_bundle_discount_rate': this.discountRate + '%',
            '_bundle_original_price': this.formatMoney(originalPrice),
            '_bundle_discounted_price': this.formatMoney(discountedPrice),
            '_bundle_savings': this.formatMoney(savings)
          }
        });
      }

      // Add to cart via Shopify Ajax API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const result = await response.json();

      // Show success message
      this.showSuccessMessage();

      // Update cart (if theme has cart update function)
      if (typeof window.updateCart === 'function') {
        window.updateCart();
      }

      // Trigger cart drawer (common in Shopify themes)
      if (typeof window.openCartDrawer === 'function') {
        window.openCartDrawer();
      } else {
        // Fallback: redirect to cart or refresh cart count
        this.updateCartCount();
      }

      // Dispatch custom event for theme integration
      document.dispatchEvent(new CustomEvent('vendor-bundle:added', {
        detail: { items, bundleIncluded: this.bundleIncluded }
      }));

    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      this.showErrorMessage(error.message);
    } finally {
      // Re-enable button
      button.disabled = false;
      button.classList.remove('loading');
      button.querySelector('.vendor-bundle-offer__button-text').textContent = originalText;
    }
  }

  /**
   * Get the current main product variant ID
   */
  getMainProductVariantId() {
    // Try to get from product form
    const productForm = document.querySelector(`form[data-product-id="${this.mainProductId}"]`) ||
                       document.querySelector('.product-form') ||
                       document.querySelector('form[action*="/cart/add"]');

    if (productForm) {
      const variantInput = productForm.querySelector('[name="id"]');
      if (variantInput) {
        return variantInput.value;
      }
    }

    // Fallback to button data
    return this.addButton?.dataset.mainVariantId;
  }

  /**
   * Observe main product variant changes
   */
  observeMainProductVariant() {
    const productForm = document.querySelector(`form[data-product-id="${this.mainProductId}"]`) ||
                       document.querySelector('.product-form');

    if (!productForm) return;

    const variantInput = productForm.querySelector('[name="id"]');
    if (!variantInput) return;

    // Listen for variant changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          this.handleMainVariantChange(variantInput.value);
        }
      });
    });

    observer.observe(variantInput, {
      attributes: true
    });

    // Also listen for change events
    variantInput.addEventListener('change', (e) => {
      this.handleMainVariantChange(e.target.value);
    });

    // Listen for price updates
    document.addEventListener('variant:change', (e) => {
      if (e.detail && e.detail.variant && e.detail.variant.price) {
        this.mainPrice = e.detail.variant.price;
        this.updateTotal();
      }
    });
  }

  /**
   * Handle main product variant change
   */
  handleMainVariantChange(newVariantId) {
    // Update button data
    if (this.addButton) {
      this.addButton.dataset.mainVariantId = newVariantId;
    }

    // Try to get new price
    const priceElement = document.querySelector('.price__regular .price-item--regular');
    if (priceElement) {
      // Extract price (remove currency symbols and convert to cents)
      const priceText = priceElement.textContent.trim();
      const priceValue = parseFloat(priceText.replace(/[^0-9.]/g, ''));
      if (!isNaN(priceValue)) {
        this.mainPrice = Math.round(priceValue * 100); // Convert to cents
        this.updateTotal();
      }
    }
  }

  /**
   * Update cart count
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update cart count elements
      const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count]');
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Show success message
   */
  showSuccessMessage() {
    const message = document.createElement('div');
    message.className = 'vendor-bundle-offer__message vendor-bundle-offer__message--success';
    message.textContent = 'Bundle added to cart!';
    this.container.appendChild(message);

    setTimeout(() => {
      message.remove();
    }, 3000);
  }

  /**
   * Show error message
   */
  showErrorMessage(errorText) {
    const message = document.createElement('div');
    message.className = 'vendor-bundle-offer__message vendor-bundle-offer__message--error';
    message.textContent = `Error: ${errorText}`;
    this.container.appendChild(message);

    setTimeout(() => {
      message.remove();
    }, 5000);
  }

  /**
   * Format money (basic implementation)
   */
  formatMoney(cents) {
    // Try to use Shopify's money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }

    // Fallback to basic formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

/**
 * Initialize all vendor bundle offers on the page
 */
function initVendorBundleOffer() {
  const containers = document.querySelectorAll('[data-vendor-bundle]');
  containers.forEach(container => {
    new VendorBundleOffer(container);
  });
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVendorBundleOffer);
} else {
  initVendorBundleOffer();
}

// Export for external use
window.VendorBundleOffer = VendorBundleOffer;
window.initVendorBundleOffer = initVendorBundleOffer;
